import { useMutation } from '@tanstack/react-query';
import { queryClient } from 'Context/QueryClientContext';
import ObrasApi from 'Data/Obras.api';
import { IObraResponse } from 'Data/interfaces/Obra/IObraListResponse';
import { NavigateFunction } from 'react-router';
import { Dispatch } from 'redux';
import toastHandler from 'Utils/toastHandler';
import { updateError } from 'Utils/Messages';
import { IUpdateConstructionSiteApiRequest } from 'Data/interfaces/Obra/IUpdateConstructionSiteApiRequest';
import { ObraActions } from 'Store/Obra/Obra.actions';
import { OBRA_QUERY_KEY } from 'Data/keys/QueryKeys';

interface IUseQueryUpdateObraProps {
  navigate: NavigateFunction;
  dispatch: Dispatch;
}

export const useQueryUpdateApiObra = ({
  navigate,
  dispatch,
}: IUseQueryUpdateObraProps) => {
  const { mutate, isPending } = useMutation({
    mutationFn: (variables: IUpdateConstructionSiteApiRequest) => {
      return ObrasApi.updateConstructionSiteApi(variables);
    },
    onSuccess: async ({ data }, variables) => {
      await Promise.all([
        queryClient.cancelQueries({ queryKey: [OBRA_QUERY_KEY] }),
        queryClient.cancelQueries({ queryKey: [OBRA_QUERY_KEY, Number(variables.CsId)] }),
      ]);

      const newData = {} as IObraResponse;
      if (variables.ExistingConstructionSiteTokenId) {
        newData.isConnectedApi = true;
      }
      if ('isArchived' in data.data) {
        newData.isArchived = data.data.isArchived;
      }

      queryClient.setQueryData<IObraResponse[]>([OBRA_QUERY_KEY], (oldData) => {
        return oldData?.map((obra) => {
          if (obra.ConstructionSiteId === variables.CsId) {
            return {
              ...obra,
              ...newData,
            };
          }
          return obra;
        });
      });
      queryClient.setQueryData<IObraResponse>(
        [OBRA_QUERY_KEY, Number(variables.CsId)],
        (oldData) => {
          if (oldData) {
            return {
              ...oldData,
              ...newData,
            };
          }
          return oldData;
        },
      );

      if (newData?.isArchived) {
        toastHandler.showSuccess('Obra arquivada com sucesso');
      }

      if (variables.isRedirect) {
        if (variables.ExistingConstructionSiteTokenId) {
          navigate(`/obras/detail/${variables.CsId}`);
        } else {
          window.location.href = data.data.redirectUrl;
        }
      } else {
        dispatch(ObraActions.listConstructionSitesStorage());
        toastHandler.showSuccess('Obra atualizada com sucesso');
      }
    },
    onError: async (error: any) => {
      const errorMsg = error.errors
        ? error.errors[0].Message
        : updateError('serviço de nuvem');
      toastHandler.showError(errorMsg);
    },
  });

  return {
    updateApiObra: mutate,
    isLoading: isPending,
  };
};
